import { HowTo } from '../types';

export const reportsHowTos: HowTo[] = [
  {
    key: 'reports.generate-pdf',
    version: 1,
    section: 'Reports',
    title: 'Generate a PDF report for a child',
    summary: 'Turn a period of published posts into a PDF you can view or download.',
    youNeed: [
      'The Reporting role',
      'At least one published post for the child in the period',
    ],
    keywords: [
      'report', 'reports', 'pdf', 'generate', 'create', 'download', 'export',
      'weekly', 'print', 'send', 'produce', 'regenerate', 'template',
    ],
    routes: ['/reports'],
    audience: 'staff',
    requiredRole: 'Reporting',
    steps: [
      {
        text: 'Open **Reports** from the menu and stay on the **Build Report** tab — **Report History** lists reports you have already generated.',
        target: ['reports.tabs', 'nav.reports'],
        route: '/reports?tab=build',
        navigateOnly: true,
      },
      {
        text: 'Choose the child. Until one is chosen the picker fills the page; after that the child lives in the rail on the left, whose expand button brings the full picker back.',
        // The picker while it fills the page; the rail once a child is chosen.
        target: ['child-picker', 'child-rail'],
      },
      {
        text: 'Pick the **Template** — which kind of report you are producing. It decides what the report contains, and how long a period it covers.',
        target: 'reports.template',
      },
      {
        text: 'Move to the period the report covers. The control matches the template: a week for a weekly report, a month for a monthly one.',
        target: 'period-nav',
      },
      {
        text: "Check the posts listed. Only **published** posts go into the report — drafts are shown so you can see what's missing, but they won't appear.",
        target: 'reports.posts-list',
      },
      {
        text: 'Open **Report Details** and fill in anything marked as required. The report can\'t be generated until those are filled.',
        target: 'reports.details',
      },
      {
        text: "Click **Generate report**. If it's greyed out, the line beneath says what's missing — usually no published posts in this period.",
        target: 'reports.generate',
      },
      {
        text: 'Use **View** or **Download** to open the PDF. If you change a post or a field afterwards, click **Regenerate** — the stored PDF keeps the old wording until you do.',
      },
    ],
    related: ['posts.publish-post'],
  },
  {
    key: 'reports.find-earlier',
    version: 1,
    section: 'Reports',
    title: 'Find a report you generated earlier',
    summary: 'Look up a report already produced, and view or download its PDF again.',
    youNeed: ['The Reporting role'],
    keywords: [
      'find', 'earlier', 'previous', 'past', 'old', 'history', 'again',
      'download', 'view', 'reprint', 'copy', 'last week', 'last month',
      'report', 'reports', 'pdf', 'lost', 'where is', 'missing',
    ],
    routes: ['/reports'],
    audience: 'staff',
    requiredRole: 'Reporting',
    steps: [
      {
        // The menu item from elsewhere, the tab strip once you are on Reports —
        // and "Take me there" still offered if you are on the wrong tab.
        text: 'Open **Reports** from the menu and go to the **Report History** tab. Build Report is for making one; History is everything already made.',
        target: ['reports.tabs', 'nav.reports'],
        route: '/reports?tab=history',
        navigateOnly: true,
      },
      {
        text: 'Set **From** and **To** to cover when it was generated, or use a quick range. History opens on the last three months, so anything older needs the dates widening.',
        target: 'history.filters',
      },
      {
        text: 'The list covers every child by default. Pick one in the rail on the left to narrow it, or leave it to see them all.',
        target: 'child-rail',
      },
      {
        text: 'Click a column heading to sort the list — by date, child or period — when it is long.',
      },
      {
        text: 'Use **Download** to save the PDF again, or open the row to read it here. A report flagged as out of date can be regenerated to pick up what has changed since.',
        target: 'history.download',
      },
    ],
    related: ['reports.generate-pdf'],
  },
];

